import { i18n } from './i18n.js';
/**
 * Game state management module
 * Manages all data related to the current game state
 */

import { DeckoutFeedback } from './ui/deckout-feedback.js';
import { TurnTimeoutFeedback } from './ui/turn-timeout-feedback.js';
import { ThinkingIndicator } from './ui/thinking-indicator.js';

const GameState = (function () {
    // Constants for local storage
    const STORAGE_KEY = 'moonfall_game_state';
    const ACTION_INDICATOR_ID = 'action-in-progress-indicator';
    const CRITICAL_PROPS = ['roomId', 'matchId', 'playerId', 'isHost', 'currentTurn', 'opponentId', 'dragSourceZone'];
    const DRAG_STATE_PROPS = new Set(['draggedCard', 'draggedCardIndex', 'dragSourceType', 'dragSourceZone']);
    function logDragStateChange(prop, value, prev) {
        if (!window.CLIENT_DEBUG_MODE || !DRAG_STATE_PROPS.has(prop)) return;
        console.log('[DRAG-STATE]', prop, '->', value, '(prev:', prev, ')', {
            draggedCard: state.draggedCard,
            dragSourceZone: state.dragSourceZone,
            dragSourceType: state.dragSourceType,
            actionInProgress: state.actionInProgress
        });
    }

    // Try to load persisted state from local storage
    function loadPersistedState() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsedState = JSON.parse(saved);
                if (window.CLIENT_DEBUG_MODE) {
                    console.log('Loaded persisted game state:', parsedState);
                }
                return parsedState;
            }
        } catch (e) {
            console.error('Failed to load persisted state:', e);
        }
        return null;
    }

    // Save critical state to local storage
    function persistCriticalState() {
        try {
            // Only save critical properties to avoid bloat
            const criticalState = {};
            CRITICAL_PROPS.forEach(prop => {
                if (state[prop] !== undefined) {
                    criticalState[prop] = state[prop];
                }
            });

            // Add timestamp to track freshness
            criticalState.timestamp = Date.now();

            localStorage.setItem(STORAGE_KEY, JSON.stringify(criticalState));
        } catch (e) {
            console.error('Failed to persist critical state:', e);
        }
    }

    // Keep an empty status region mounted so assistive technology observes a
    // later text change instead of discovering an already-populated live region.
    function ensureActionIndicator() {
        let indicator = document.getElementById(ACTION_INDICATOR_ID);
        if (!indicator && document.body) {
            indicator = document.createElement('div');
            indicator.id = ACTION_INDICATOR_ID;
            indicator.setAttribute('role', 'status');
            indicator.setAttribute('aria-live', 'polite');
            indicator.setAttribute('aria-atomic', 'true');
            document.body.appendChild(indicator);
        }
        return indicator;
    }

    function clearActionIndicator() {
        const indicator = document.getElementById(ACTION_INDICATOR_ID);
        if (!indicator) return;
        indicator.textContent = '';
        indicator.classList.remove('game-notification', 'processing-indicator');
    }

    /* A caller may ask for a GRACE before the processing chrome: a card the
       player has just dragged and spent plays its own commit where it was let
       go, and the centred toast used to land on top of that moment — and on a
       fast answer it flashed for a few frames and went. The input lock is never
       deferred; only the words are, and only while this action is running. */
    let actionIndicatorGraceTimer = null;

    function showActionIndicator(indicator) {
        if (!indicator) return;
        indicator.classList.add('game-notification', 'processing-indicator');
        // Key-aware: t() answers with the KEY when a translation is missing or
        // not loaded, and a key is truthy, so `|| fallback` alone put
        // `game.processingAction` on screen.
        const processing = i18n?.t?.('game.processingAction');
        indicator.textContent = processing && processing !== 'game.processingAction'
            ? processing
            : 'Processing action...';
    }

    function clearActionIndicatorGrace() {
        if (actionIndicatorGraceTimer !== null) clearTimeout(actionIndicatorGraceTimer);
        actionIndicatorGraceTimer = null;
    }

    // Load previously persisted state or create empty state
    const persistedState = loadPersistedState();

    // Private game state object
    let state = {
        roomId: null,
        matchId: null,
        playerId: null,
        isHost: false,
        ready: false,
        state: null, // Game phase: 'mulligan', 'playing', etc.
        winner: null,
        endReason: null,
        currentTurn: 0,
        isYourTurn: false,
        draggedCard: null,
        draggedCardIndex: null,
        dragSourceType: null,
        dragSourceZone: null,
        opponentId: null,
        actionInProgress: false,
        // When true, an in-progress action shows only the functional input lock,
        // never the "Processing action…" chrome (e.g. the player's own summons).
        actionIndicatorSilent: false,
        safetyTimeoutId: null,
        summonedThisTurn: [],
        usedThisTurn: [],
        lastUpdateTime: Date.now(),
        // User authentication state
        user: null, // { id, username, subscriptionType, stardust }
        selectedDeckId: null // Selected deck for next game (null = random)
    };

    // If we have persisted state that's less than 1 hour old, restore critical properties
    if (persistedState && persistedState.timestamp) {
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        const age = Date.now() - persistedState.timestamp;

        if (age < oneHour) {
            if (window.CLIENT_DEBUG_MODE) {
                console.log('Restoring critical state from local storage (age: ' +
                    Math.round(age / 1000 / 60) + ' minutes)');
            }

            // Restore only critical properties
            CRITICAL_PROPS.forEach(prop => {
                if (persistedState[prop] !== undefined) {
                    state[prop] = persistedState[prop];
                }
            });
        } else {
            if (window.CLIENT_DEBUG_MODE) {
                console.log('Persisted state is too old (' +
                    Math.round(age / 1000 / 60) + ' minutes), not restoring');
            }
            // Clear outdated storage
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    ensureActionIndicator();

    // Public API
    return {
        /**
         * Get the current game state
         * @returns {Object} The current game state
         */
        get: function () {
            return { ...state };
        },

        /**
         * Set a specific game state property
         * @param {string} prop - The property to set
         * @param {*} value - The value to set
         */
        set: function (prop, value) {
            if (prop in state) {
                logDragStateChange(prop, value, state[prop]);
                state[prop] = value;
            }
        },

        /**
         * Update multiple game state properties at once
         * @param {Object} newState - Object containing properties to update
         */
        update: function (newState) {
            Object.assign(state, newState);

            // Ensure opponentId stays in sync with the opponent object for convenience
            if (newState.opponent && typeof newState.opponent === 'object' && newState.opponent.id) {
                state.opponentId = newState.opponent.id;
            }
            // Allow direct override as well
            if (newState.opponentId !== undefined) {
                state.opponentId = newState.opponentId;
            }

            state.lastUpdateTime = Date.now();

            // If any critical properties changed, persist to local storage
            let criticalPropsChanged = false;
            CRITICAL_PROPS.forEach(prop => {
                if (newState[prop] !== undefined) {
                    criticalPropsChanged = true;
                }
            });

            if (criticalPropsChanged) {
                persistCriticalState();
            }
        },

        /**
         * Reset the game state to its initial values
         */
        reset: function () {
            const sessionUser = state.user || null;
            const sessionDeckId = state.selectedDeckId || null;
            state = {
                roomId: null,
                matchId: null,
                playerId: null,
                isHost: false,
                ready: false,
                state: null,
                winner: null,
                endReason: null,
                currentTurn: 0,
                isYourTurn: false,
                draggedCard: null,
                draggedCardIndex: null,
                dragSourceType: null,
                dragSourceZone: null,
                opponentId: null,
                actionInProgress: false,
                actionIndicatorSilent: false,
                safetyTimeoutId: null,
                summonedThisTurn: [],
                usedThisTurn: [],
                lastUpdateTime: Date.now(),
                user: sessionUser,
                selectedDeckId: sessionDeckId
            };

            // Clear persisted state when resetting
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch (e) {
                console.error('Failed to clear persisted state:', e);
            }

            document.body.classList.remove('action-in-progress');
            clearActionIndicatorGrace();
            clearActionIndicator();

            // Quiet the portrait feedback module and stop its timers.
            ThinkingIndicator?.reset?.();
            DeckoutFeedback?.reset?.();
            TurnTimeoutFeedback?.reset?.();
        },

        /**
         * Set action in progress state and manage the UI indicator.
         * @param {boolean} inProgress - Whether an action is in progress
         * @param {{silent?: boolean, graceMs?: number}} [options] - `silent: true` latches a
         *   quiet action (e.g. the player's own summon): the functional input
         *   lock still applies, but no "Processing action…" chrome is shown. The
         *   latch persists across the round-trip so the server's later
         *   actionInProgress mirror can't resurrect the indicator, and clears
         *   when the action ends. `graceMs` defers that chrome: it appears
         *   only if the action is still running once the grace is over.
         */
        setActionInProgress: function (inProgress, options) {
            state.actionInProgress = inProgress;

            if (inProgress) {
                if (options && options.silent) state.actionIndicatorSilent = true;
                document.body.classList.add('action-in-progress');
                const indicator = ensureActionIndicator();
                const graceMs = Number(options && options.graceMs);
                if (state.actionIndicatorSilent) {
                    // Quiet action — keep the input lock and mounted live region,
                    // but never show or announce processing chrome.
                    clearActionIndicatorGrace();
                    clearActionIndicator();
                } else if (Number.isFinite(graceMs) && graceMs > 0) {
                    // Deferred chrome: shown only if this action outlasts its grace.
                    clearActionIndicatorGrace();
                    clearActionIndicator();
                    actionIndicatorGraceTimer = setTimeout(() => {
                        actionIndicatorGraceTimer = null;
                        if (state.actionInProgress && !state.actionIndicatorSilent) {
                            showActionIndicator(ensureActionIndicator());
                        }
                    }, graceMs);
                } else if (actionIndicatorGraceTimer !== null) {
                    // The server mirroring an action still inside its grace:
                    // the grace stands.
                } else if (indicator) {
                    showActionIndicator(indicator);
                }
            } else {
                state.actionIndicatorSilent = false;
                clearActionIndicatorGrace();
                document.body.classList.remove('action-in-progress');
                clearActionIndicator();
            }
        },

        /**
         * Check if a card can be used based on game rules
         * @param {string} cardId - The card instance ID to check
         * @returns {boolean} Whether the card can be used
         */
        canUseCard: function (cardId) {
            const wasJustSummoned = state.summonedThisTurn.includes(cardId);
            const wasAlreadyUsed = state.usedThisTurn.includes(cardId);
            try {
                const board = (state.player && Array.isArray(state.player.summonedCards)) ? state.player.summonedCards : [];
                const card = board.find(c => c && c.instanceId === cardId);
                // If LLM/server set canAttack explicitly true, allow regardless of default turn constraints
                if (card && card.canAttack === true) {
                    return true;
                }
            } catch (_) { }
            // Default rule when no explicit override
            return !wasJustSummoned && !wasAlreadyUsed;
        },

        /**
         * Get session age in seconds
         * @returns {number} Session age in seconds
         */
        getSessionAge: function () {
            return Math.round((Date.now() - state.lastUpdateTime) / 1000);
        },

        /**
         * Force persistence of current state
         */
        forcePersist: function () {
            persistCriticalState();
        },

        /**
         * Set the authenticated user
         * @param {Object} user - User data from auth { id, username, subscriptionType, stardust }
         */
        setUser: function (user) {
            state.user = user;
        },

        /**
         * Get the authenticated user
         * @returns {Object|null} User data or null if not authenticated
         */
        getUser: function () {
            return state.user;
        },

        /**
         * Set the selected deck for next game
         * @param {string|null} deckId - Deck ID or null for random
         */
        setSelectedDeck: function (deckId) {
            state.selectedDeckId = deckId;
            if (window.CLIENT_DEBUG_MODE) {
                console.log('[GameState] Selected deck:', deckId || 'random');
            }
        },

        /**
         * Get the selected deck ID
         * @returns {string|null} Deck ID or null for random
         */
        getSelectedDeck: function () {
            return state.selectedDeckId;
        }
    };
})();

// Expose the shared instance to independently loaded UI modules and tests.

// Track added stories to prevent duplicates
const addedStories = new Set();

// Track elements that already have drop event listeners
const dropTargetsInitialized = new Set();

export { GameState };
